import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { WorkflowStatusChip } from 'components/common/WorkflowStatusChip';
import { WorkflowProvider } from 'components/Workflow/WorkflowContext';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { hasFunctionality } from 'components/authorization/PermissionUtils';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { handleAPIError } from 'common/errorHandler';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { formatDateInvoices } from 'common/utils';
import Spinner from 'components/Spinner';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

interface Props {
  entityId: number;
  entityTypeId: number;
  issuedByEntity: boolean;
  workflowModuleId: number;
}

const EntityInvoiceListInner = ({ entityId, entityTypeId, issuedByEntity, workflowModuleId }: Props) => {
  const api: Api = useApi();
  const toast = useToastMessageQueue();
  const canSeeLedger = hasFunctionality('ui.ledger.view', 'view');

  const [rows, setRows] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = usePaginationPreference('entityInvoiceList', 10);

  const [seatsDialogOpen, setSeatsDialogOpen] = useState(false);
  const [seatsDialogRow, setSeatsDialogRow] = useState<any>(null);
  const [seats, setSeats] = useState<any[]>([]);
  const [seatsLoading, setSeatsLoading] = useState(false);

  const fetchInvoices = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const response = await api.invoiceApiWrapper.searchInvoicesByEntity(
        '1',
        entityId,
        entityTypeId,
        issuedByEntity,
        '',
        (pagination.page ?? 0) + 1,
        pagination.pageSize ?? 10
      );
      const data = response.data?.data;
      setRows(data?.queryResult ?? []);
      setTotalRecords(data?.totalRecords ?? 0);
    } catch (error) {
      handleAPIError(error, toast, {});
    } finally {
      setLoading(false);
    }
  }, [entityId, entityTypeId, issuedByEntity, pagination.page, pagination.pageSize]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleOpenSeats = useCallback(async (row: any) => {
    setSeatsDialogRow(row);
    setSeatsDialogOpen(true);
    setSeats([]);
    setSeatsLoading(true);
    try {
      const response = await api.invoiceApiWrapper.getInvoiceLinkedSeats('1', row.id);
      setSeats(response.data?.data ?? []);
    } catch {
      setSeats([]);
    } finally {
      setSeatsLoading(false);
    }
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      renderCell: (params) => <>{formatDateInvoices(params.row.date)}</>,
    },
    {
      field: 'number',
      headerName: 'Invoice #',
      flex: 1,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      renderCell: (params) => (
        <>{params.row.currency?.code} {params.row.amount?.toLocaleString()}</>
      ),
    },
    {
      field: 'idWorkflowState',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => <WorkflowStatusChip stateId={params.row.idWorkflowState} />,
    },
  ];

  if (canSeeLedger) {
    columns.push({
      field: '_seats',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => handleOpenSeats(params.row)}>
          <AccountBalanceIcon fontSize="small" />
        </IconButton>
      ),
    });
  }

  return (
    <Box>
      <div className="card-header">
        <Typography variant="h6" color="primary" display="flex" alignItems="center" gap={1}>
          <ReceiptLongIcon className="mb-1" />
          {issuedByEntity ? 'Issued Invoices' : 'Received Invoices'}
        </Typography>
      </div>
      {loading ? (
        <Box style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spinner small={true} />
        </Box>
      ) : (
        <div className="card-body">
          <Box style={{ width: '100%', display: 'flex' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              rowCount={totalRecords}
              paginationMode="server"
              paginationModel={pagination}
              onPaginationModelChange={setPagination}
              pageSizeOptions={[5, 10, 20]}
              getRowId={(row) => row.id}
              autoHeight
            />
          </Box>
        </div>
      )}

      <Dialog open={seatsDialogOpen} onClose={() => setSeatsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Payments linked to invoice {seatsDialogRow?.number ?? ''}
        </DialogTitle>
        <DialogContent>
          {seatsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : seats.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={1}>
              No payments linked
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {seats.map((seat) => (
                  <TableRow key={seat.id}>
                    <TableCell>
                      {seat.account?.name ?? '-'}
                      {seat.account?.accountNumber ? ` (${seat.account.accountNumber})` : ''}
                    </TableCell>
                    <TableCell>{formatDateInvoices(seat.date)}</TableCell>
                    <TableCell>{seat.description}</TableCell>
                    <TableCell>
                      {seat.currency?.code} {seat.amount?.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const EntityInvoiceList = (props: Props) => (
  <PermissionsGate
    identifier="ui.accountspayable.read"
    entityType={1}
    viewRoles={[]}
    editRoles={[]}
    RenderError={() => null}
  >
    <WorkflowProvider moduleId={props.workflowModuleId}>
      <EntityInvoiceListInner {...props} />
    </WorkflowProvider>
  </PermissionsGate>
);

export default EntityInvoiceList;
